import { NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Signal, Type } from '@angular/core';
import { BannerItemModel, SliderContentModel, SliderItemModel } from '@libs/cms/models';
import { SliderComponent, SliderSlideDirective } from '@libs/common/components';
import { ImageItemSourceModel } from '@libs/common/models';
import { provideContentTrackingContext } from '@libs/shared/tracking';
import { ContentBaseComponent } from '../content-base.component';
import { SlideComponent, SlideItemComponentMap } from './content-slide-map';

interface SliderItemViewModel {
  component: Type<SlideComponent>;
  data: SliderItemModel;
}

@Component({
  selector: 'ui-content-slider',
  templateUrl: './content-slider.component.html',
  styleUrls: ['./content-slider.component.scss', './content-slider.component.lekkerland-2026.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SliderComponent,
    SliderSlideDirective,
    NgClass,
    NgComponentOutlet,
  ],
  host: {
    '[class.has-navigation]': 'hasNavigation()',
  },
  providers: [
    provideContentTrackingContext(ContentSliderComponent),
  ],
})
export class ContentSliderComponent extends ContentBaseComponent<SliderContentModel> {
  public readonly hasNavigation: Signal<boolean> = computed(() => {
    return this.data().options.navigation && this.data().items.length > 1;
  });

  public readonly isSingleBanner: Signal<boolean> = computed(() => {
    return this.data().category === 'banner' && this.data().items.length === 1;
  });

  public readonly sliderViews: Signal<SliderItemViewModel[]> = computed(() => {
    const items: SliderItemModel[] = this.isSingleBanner()
      ? [this.formatSingleBanner(this.data())]
      : this.data().items;

    return items.map(item => ({
      data: item,
      component: SlideItemComponentMap[item.type],
    }));
  });

  private formatSingleBanner(data: SliderContentModel): BannerItemModel {
    const item: BannerItemModel = data.items[0] as BannerItemModel;

    const sourceSingle: ImageItemSourceModel = item.image.source.find(
      (element: ImageItemSourceModel) => element.type === 'large',
    ) as ImageItemSourceModel;

    let src: string = item.image.src;

    if ('animation' !== item.image.type) {
      src = sourceSingle.srcset.split(' ').slice(-2)[0];
    }

    return {
      ...item,
      type: 'banner',
      image: {
        ...item.image,
        src,
        source: [sourceSingle],
      },
    };
  }
}
